import { useEffect, useRef, useState } from 'react';
import AgoraRTC, {
  ClientRole,
  DeviceInfo,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  NetworkQuality,
  UID
} from 'agora-rtc-sdk-ng';
import { isAndroid, isIOS } from 'react-device-detect';

import { ActiveUser, FacingMode, OnStageUser, PinMode, RTCUser, Volume } from 'types';
import {
  ACTIVE_VOLUME_THRESHOLD,
  convertDateToTimestamp,
  diffTimestamps,
  mapAgoraRemoteUserToRTCUser,
  SELECTED_ACTIVE_SPEAKER_TIMEOUT
} from 'utils';
import { RTCService } from 'services';

export type RTCState = {
  joining: boolean;
  joined: boolean;
  published: boolean;
  remoteUsers: RTCUser[];
};

export type RTCEventHandlers = {
  onJoined?(uid: UID): void;
  onChangeActiveUser?(cb: (prevActiveUser: ActiveUser) => ActiveUser): void;
  onNetworkQualityChanged?(isStable: boolean): void;
  onClientRoleChanged?(newClientRole: ClientRole): void;
  onAutoplayBlocked?(): void;
};

export type RTCOptions = RTCEventHandlers & {
  clientRole: ClientRole;
};

export type RTCResult = {
  uid?: UID;
  audioTrack?: IMicrophoneAudioTrack;
  videoTrack?: ICameraVideoTrack;
  state: RTCState;
  join(uid: UID, token?: string | null): Promise<void>;
  leave(): Promise<void>;
  muteMicrophone(): Promise<void>;
  unmuteMicrophone(): Promise<void>;
  muteCamera(): Promise<void>;
  unmuteCamera(): Promise<void>;
  resumeAudio(): void;
  changeFacingMode(facingMode: FacingMode): Promise<FacingMode>;
  pushStreamToCDN(rtmpURL: string, stageUsers: OnStageUser[]): Promise<void>;
  stopStreamToCDN(rtmpURL: string): Promise<void>;
  updateTranscodingConfig(stageUsers: OnStageUser[]): Promise<void>;
  publishLocalTracks(audioEnabled: boolean, videoEnabled: boolean): Promise<void>;
};

export const useRTC = (
  channelName: string,
  { clientRole, onJoined, onChangeActiveUser, onNetworkQualityChanged, onClientRoleChanged }: RTCOptions
): RTCResult => {
  const [state, setState] = useState<RTCState>({
    joining: false,
    joined: false,
    published: false,
    remoteUsers: []
  });

  const localUID = useRef<UID | null>(null);
  const unintendedVideoMutedRef = useRef<NodeJS.Timeout | null>(null);

  const join = async (uid: UID, token: string | null = null) => {
    setState(prev => ({ ...prev, joined: false, joining: true }));

    await RTCService.setClientRole('host');
    localUID.current = await RTCService.join(channelName, uid, token);

    RTCService.enableAudioVolumeIndicator();

    setState(prev => ({ ...prev, joined: true, joining: false }));

    onJoined?.(uid);

    if (unintendedVideoMutedRef.current) {
      clearInterval(unintendedVideoMutedRef.current);
    }
    unintendedVideoMutedRef.current = setInterval(recoverMutedVideo, 3_000);
  };

  const leave = async () => {
    await RTCService.leave(channelName);

    setState(prev => ({ ...prev, joined: false, published: false }));
    localUID.current = null;

    if (unintendedVideoMutedRef.current) {
      clearInterval(unintendedVideoMutedRef.current);
    }
  };

  const publishLocalTracks = async (audioEnabled: boolean, videoEnabled: boolean) => {
    const [microphoneTrack, cameraTrack] = await RTCService.createLocalTracks(audioEnabled, videoEnabled);

    const promises: Promise<void>[] = [];

    if (audioEnabled && microphoneTrack) {
      promises.push(RTCService.publishLocalTrack(microphoneTrack));
    }
    if (videoEnabled && cameraTrack) {
      promises.push(RTCService.publishLocalTrack(cameraTrack));
    }

    await Promise.all(promises);
    setState(prev => ({ ...prev, published: true }));
  };

  const muteMicrophone = () => RTCService.muteMicrophone();

  const unmuteMicrophone = () => RTCService.unmuteMicrophone();

  const muteCamera = () => RTCService.muteCamera();

  const unmuteCamera = () => RTCService.unmuteCamera();

  const resumeAudio = () => {
    state.remoteUsers.forEach(remoteUser => {
      remoteUser.audioTrack && RTCService.stopAudio(remoteUser.audioTrack as IRemoteAudioTrack);
      remoteUser.audioTrack && RTCService.playAudio(remoteUser.audioTrack as IRemoteAudioTrack);
    });
  };

  const changeFacingMode = (facingMode: FacingMode) => RTCService.changeFacingMode(facingMode);

  const pushStreamToCDN = (rtmpURL: string, stageUsers: OnStageUser[]) =>
    RTCService.startLiveStreaming(rtmpURL, stageUsers);

  const stopStreamToCDN = (rtmpURL: string) => RTCService.stopLiveStreaming(rtmpURL);

  const recoverMutedVideo = async () => {
    const sdkMuted = RTCService.cameraTrack?.muted;
    const trackMuted = RTCService.cameraTrack?.getMediaStreamTrack().muted;
    const isMutedUnintendedly = !sdkMuted && trackMuted;

    if (isMutedUnintendedly) {
      await RTCService.cameraTrack?.setEnabled(false);
      await RTCService.cameraTrack?.setEnabled(true);
    }
  };

  const updateTranscodingConfig = (users: OnStageUser[]) => RTCService.updateTranscodingConfig(users);

  // remote users joined/left effect
  useEffect(() => {
    const handleOnRemoteUserJoined = (remoteUser: IAgoraRTCRemoteUser) => {
      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const handleOnRemoteUserLeft = (remoteUser: IAgoraRTCRemoteUser) => {
      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const { client } = RTCService;

    client.on('user-joined', handleOnRemoteUserJoined);
    client.on('user-left', handleOnRemoteUserLeft);

    return () => {
      client.off('user-joined', handleOnRemoteUserJoined);
      client.off('user-left', handleOnRemoteUserLeft);
    };
  }, [clientRole]);

  // remote users joined/left/published/unpublished effect
  useEffect(() => {
    const handleOnRemoteUserJoined = async (remoteUser: IAgoraRTCRemoteUser) => {
      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const handleOnRemoteUserLeft = async (remoteUser: IAgoraRTCRemoteUser) => {
      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const handleOnRemoteUserPublished = async (remoteUser: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      await RTCService.subscribe(remoteUser, mediaType);

      if (mediaType === 'audio') {
        if (remoteUser.audioTrack) RTCService.playAudio(remoteUser.audioTrack);
      }

      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const handleOnRemoteUserUnpublished = async (remoteUser: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      await RTCService.unsubscribe(remoteUser, mediaType);

      if (mediaType === 'audio') {
        if (remoteUser.audioTrack) RTCService.stopAudio(remoteUser.audioTrack);
      }

      setState(prev => ({ ...prev, remoteUsers: RTCService.client.remoteUsers.map(mapAgoraRemoteUserToRTCUser) }));
    };

    const { client } = RTCService;

    client.on('user-joined', handleOnRemoteUserJoined);
    client.on('user-left', handleOnRemoteUserLeft);
    client.on('user-published', handleOnRemoteUserPublished);
    client.on('user-unpublished', handleOnRemoteUserUnpublished);

    return () => {
      client.off('user-joined', handleOnRemoteUserJoined);
      client.off('user-left', handleOnRemoteUserLeft);
      client.off('user-published', handleOnRemoteUserPublished);
      client.off('user-unpublished', handleOnRemoteUserUnpublished);
    };
  }, [RTCService.client.uid]);

  // close local tracks when unmounted
  useEffect(
    () => () => {
      RTCService.closeLocalTracks();
    },
    []
  );

  useEffect(() => {
    if (!state.joined) return () => {};

    const handleOnNetworkQualityChanged = (stats: NetworkQuality) => {
      const isStable = stats.downlinkNetworkQuality <= 3 || stats.uplinkNetworkQuality <= 3;

      onNetworkQualityChanged?.(isStable);
    };

    RTCService.client.on('network-quality', handleOnNetworkQualityChanged);

    return () => {
      RTCService.client.off('network-quality', handleOnNetworkQualityChanged);
    };
  }, [state.joined]);

  // on microphone changed effect
  useEffect(() => {
    if (isAndroid) {
      const handleOnMicrophoneChanged = async (changedDevice: DeviceInfo) => {
        const allDevices = await AgoraRTC.getMicrophones();
        const filteredDevices = allDevices.filter(device => !device.label.toLowerCase().match(/earpiece|default/g));

        let newDevice: MediaDeviceInfo | null = null;

        switch (changedDevice.state) {
          case 'ACTIVE': {
            if (changedDevice.device.label.toLowerCase().match(/earpiece/g)) {
              newDevice = filteredDevices[filteredDevices.length - 1];
            } else {
              newDevice = changedDevice.device;
            }
            break;
          }
          case 'INACTIVE': {
            if (changedDevice.device.label.toLowerCase().match(/earpiece/g)) {
              // do nothing
            } else {
              newDevice = filteredDevices[filteredDevices.length - 1];
            }
            break;
          }
        }

        if (newDevice) {
          RTCService.microphoneTrack?.setDevice(newDevice.deviceId);
        }
      };

      AgoraRTC.onMicrophoneChanged = handleOnMicrophoneChanged;
    } else if (isIOS) {
      // iOS has device auto-switching, so no need to handle explicitly
    }
  }, []);

  // change active speaker effect
  useEffect(() => {
    const handleVolumeIndicator = (volumes: Volume[]) => {
      const sortedVolumes = volumes
        .sort((a, b) => b.level - a.level)
        .filter(({ uid }) => uid.toString() !== RTCService.client.uid?.toString());
      const loudestVolume = sortedVolumes[0];

      if (!loudestVolume) return;

      // change main speaker

      onChangeActiveUser?.(prevActiveUser => {
        if (prevActiveUser.uid === loudestVolume.uid.toString() || loudestVolume.level < ACTIVE_VOLUME_THRESHOLD) {
          return prevActiveUser;
        }

        if (prevActiveUser.pinned) {
          // do nothing
        } else if (prevActiveUser.pinMode === PinMode.AUTO) {
          return {
            uid: loudestVolume.uid.toString(),
            pinMode: PinMode.AUTO,
            pinned: false
          };
        } else {
          const tsPinned = prevActiveUser.pinnedAt;
          const tsNow = convertDateToTimestamp(new Date());
          const tsDiffInMs = diffTimestamps(tsNow, tsPinned);
          const isManualPinExpired = tsDiffInMs >= SELECTED_ACTIVE_SPEAKER_TIMEOUT;

          if (isManualPinExpired) {
            return {
              uid: loudestVolume.uid.toString(),
              pinMode: PinMode.AUTO,
              pinned: false
            };
          }
        }

        return prevActiveUser;
      });

      // change stage users isSpeaking field

      let volumesMap: { [id: string]: Volume } = {};

      sortedVolumes.forEach(volume => {
        volumesMap = { ...volumesMap, [volume.uid.toString()]: volume };
      });

      setState(prev => ({
        ...prev,
        remoteUsers: prev.remoteUsers.map(remoteUser => ({
          ...remoteUser,
          isSpeaking: volumesMap[remoteUser.uid.toString()]?.level > ACTIVE_VOLUME_THRESHOLD
        }))
      }));
    };

    const { client } = RTCService;

    client.on('volume-indicator', handleVolumeIndicator);

    return () => {
      client.off('volume-indicator', handleVolumeIndicator);
    };
  }, [onChangeActiveUser]);

  // on client role change effect
  useEffect(() => {
    if (state.joined) {
      onClientRoleChanged?.(clientRole);
    }
  }, [clientRole]);

  return {
    uid: localUID.current || undefined,
    audioTrack: RTCService.microphoneTrack || undefined,
    videoTrack: RTCService.cameraTrack || undefined,
    state,
    join,
    leave,
    muteMicrophone,
    unmuteMicrophone,
    muteCamera,
    unmuteCamera,
    resumeAudio,
    changeFacingMode,
    pushStreamToCDN,
    stopStreamToCDN,
    updateTranscodingConfig,
    publishLocalTracks
  };
};
